import React, { useEffect, useState } from "react"
import Button from "components/Form/Button"
import { useMutation } from "@tanstack/react-query"
import { fetchTicket } from "api/findTicket"
import TicketModal from "components/Modal/TicketModal"
import { useLang } from "hooks/useLang"
import { MainLayout } from "components/Layout"
import InputNew from "components/Form/InputNew"
import PhoneComponent from "components/PhoneInput/PhoneComponent"
import ticketNotFound from "lottiefiles/no_orders_found.json"

import { AnimatePresence } from "framer-motion"
import { useRouter } from "next/router"

import dynamic from "next/dynamic"

const Lottie = dynamic(() => import('lottie-react'), { ssr: false });

interface IForm {
    ticket_number: string
    email: string
    phone: string
    calling_code: string
}

const FindMyTicket = () => {
    const { t } = useLang()
    const { push } = useRouter()
    const [isOpen, setOpenModal] = useState(false)
    const [searchLabel, setSearchLabel] = useState("phone")

    const [form, setForm] = useState<IForm>({
        ticket_number: "",
        email: "",
        phone: "",
        calling_code: "966",
    })

    const { mutate, isPending, data, isError } = useMutation({
        mutationFn: fetchTicket,
    })

    useEffect(() => {
        if (data?.payload) {
            setOpenModal(true)
        }
    }, [data?.payload])

    const onChange = (e: { target: { name: any; value: any } }) => {
        const { name, value } = e.target

        setForm((prev) => ({ ...prev, [name]: value }))
    }

    async function HandleSubmit(e: { preventDefault: () => void }) {
        e.preventDefault()

        if (form.ticket_number === "") {
            return
        } else {
            mutate(
                searchLabel === "email"
                    ? { ticket_number: form.ticket_number, email: form.email }
                    : {
                          ticket_number: form.ticket_number,
                          phone: form.phone,
                          calling_code: form.calling_code,
                      }
            )
        }
    }

    function ShowInput() {
        if (searchLabel === "phone") {
            return (
                <PhoneComponent
                    phoneNumber={form.phone}
                    callingCode={form.calling_code}
                    setPhoneNumber={(phone) =>
                        setForm((prev) => ({
                            ...prev,
                            phone: phone,
                        }))
                    }
                    setCallingCode={(callingCode) =>
                        setForm((prev) => ({
                            ...prev,
                            calling_code: callingCode,
                        }))
                    }
                    reqStar
                />
            )
        }

        return (
            <InputNew
                label={t("lb_email")}
                name="email"
                placeholder={t("pl_email")}
                type="text"
                required
                reqStar
                value={form.email}
                onChange={onChange}
            />
        )
    }

    return (
        <MainLayout>
            <div className="layout py-32">
                <p className="p-5 text-center text-base font-bold md:text-lg">
                    {t("FindMyTicket_des")}
                </p>

                {isError ? (
                    <div className="flex flex-col items-center gap-5">
                        <div className="mx-auto flex w-full max-w-6xl flex-col items-center gap-5 p-5">
                            <Lottie
                                animationData={ticketNotFound}
                                className="h-[180px] w-[180px] md:h-[230px] md:w-[230px]"
                            />
                            <span className="items-center text-center text-sm text-warning-theme-400 dark:text-warning-theme-400 md:text-base">
                                {t("no_tickets_exist")}
                            </span>
                        </div>

                        <Button
                            className={`w-full max-w-[200px] p-2`}
                            onClick={() => push("/")}
                            text={t("backToHome")}
                        />
                    </div>
                ) : (
                    <div className="mx-auto flex max-w-md flex-col py-5">
                        <div className="flex">
                            <button
                                className={`tab w-full ${
                                    searchLabel === "phone" && "active"
                                }`}
                                onClick={() => {
                                    setSearchLabel("phone")
                                }}>
                                {t("Support_MobileNumber")}
                            </button>
                            <button
                                className={`tab w-full ${
                                    searchLabel === "email" && "active"
                                }`}
                                onClick={() => {
                                    setSearchLabel("email")
                                }}>
                                {t("email")}
                            </button>
                        </div>

                        <form
                            className="mx-auto flex w-full flex-col gap-5 py-5"
                            onSubmit={HandleSubmit}>
                            <InputNew
                                label={t("Support_ticket_Number")}
                                name="ticket_number"
                                placeholder={"ex: 3883283093"}
                                type="text"
                                required
                                value={form.ticket_number}
                                reqStar
                                onChange={onChange}
                            />
                            {ShowInput()}
                            <div className="mt-5 flex justify-center">
                                <Button
                                    className={`w-full p-2`}
                                    onClick={HandleSubmit}
                                    text={t("Support_find_ticket")}
                                    size="large"
                                    type="submit"
                                    loading={isPending}
                                    disabled={isPending}
                                />
                            </div>
                        </form>
                    </div>
                )}

                <AnimatePresence>
                    {isOpen ? (
                        <TicketModal
                            ticket_end_time={data?.payload.end_time}
                            ticket_id={data?.payload._id}
                            ticket_number={data?.payload.ticket_number}
                            ticket_price={data?.payload.price}
                            ticket_start_time={data?.payload.start_time}
                            ticket_status={data?.payload.status}
                            ticket_title={data?.payload.ticket_type_title}
                            ticket_token={data?.payload.token}
                            event_city={data?.payload.event_id?.address.city}
                            starting_date={data?.payload.start_time}
                            setOpenModal={setOpenModal}
                            event_title={data?.payload.event_id?.en_title}
                            seat_id={data?.payload.seat_id}
                        />
                    ) : null}
                </AnimatePresence>
            </div>
        </MainLayout>
    )
}

export default FindMyTicket
